import { Phone } from "lucide-react";
import type { ChatMessage } from "@/types/chat";
import type { ChatCallHistoryMetadata } from "@/types/chatCall";

interface CallHistoryMessageProps {
  message: ChatMessage;
  currentUserId?: number;
  isOwnMessage: boolean;
}

function formatDuration(durationSeconds?: number) {
  const totalSeconds = Math.max(0, Math.floor(durationSeconds || 0));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

function getMetadata(message: ChatMessage) {
  return (message.metadata || null) as ChatCallHistoryMetadata | null;
}

function getDirectionLabel(
  metadata: ChatCallHistoryMetadata | null,
  currentUserId: number | undefined,
  isOwnMessage: boolean,
) {
  if (!metadata || !currentUserId) {
    return isOwnMessage
      ? "Chamada de voz realizada"
      : "Chamada de voz recebida";
  }

  if (metadata.callerId === currentUserId) {
    return "Chamada de voz realizada";
  }

  if (metadata.calleeId === currentUserId) {
    return "Chamada de voz recebida";
  }

  return "Chamada de voz";
}

function getStatusLabel(metadata: ChatCallHistoryMetadata | null) {
  switch (metadata?.status) {
    case "declined":
      return "Recusada";
    case "cancelled":
      return "Cancelada";
    case "missed":
      return "Não atendida";
    case "ended":
    case "in_progress":
      return metadata.durationSeconds
        ? `Duração: ${formatDuration(metadata.durationSeconds)}`
        : "Encerrada";
    case "answered":
      return "Atendida";
    case "connection_failed":
      return "Falha de conexão";
    case "microphone_denied":
      return "Microfone sem permissão";
    case "recipient_unavailable":
      return "Usuário indisponível";
    default:
      return "Em andamento";
  }
}

export function CallHistoryMessage({
  message,
  currentUserId,
  isOwnMessage,
}: CallHistoryMessageProps) {
  const metadata = getMetadata(message);
  const directionLabel = getDirectionLabel(metadata, currentUserId, isOwnMessage);
  const statusLabel = getStatusLabel(metadata);

  return (
    <div className="flex items-start gap-2">
      <span
        className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
          isOwnMessage ? "bg-white/15 text-white" : "bg-[#153ECE]/10 text-[#153ECE]"
        }`}
      >
        <Phone size={14} />
      </span>

      <div className="min-w-0">
        <p className="text-sm font-medium leading-5">{directionLabel}</p>
        <p
          className={`mt-0.5 text-xs leading-5 ${
            isOwnMessage ? "text-white/80" : "text-gray-500"
          }`}
        >
          {statusLabel}
        </p>
      </div>
    </div>
  );
}
